import React from "react";
import "./Portfolio.css";
import Helmet from "react-helmet";
import ProfileOverview from "../../Components/ProfileOverview/ProfileOverview.js";
import PortfolioProject from "../../Components/Portfolio/PortfolioProject.js";
import TillerDesignExpoPoster from "./documents/ME-270-Design-Expo-Poster.pdf";
import AgOEMFixtureImg from "./1.png";
import TurfProgressiveFixtureImg1 from "./2.png";
import TurfProgressiveFixtureImg2 from "./3.png";
import TurfProgressiveFixtureImg3 from "./4.png";
import TurfPokaYokeFixtureImg from "./5.png";
import TurfLeftRightFixtureImg1 from "./6.png";
import TurfLeftRightFixtureImg2 from "./7.png";
import CoolantTrayIsoImg from "./coolant tray (iso).png";
import CoolantTraySideImg from "./coolant tray (side).png";
import INPRImg from "./INPR1.png";
import INPRCoverageImg from "./coverage.png";
import TillerCADImg from "./tiller - CAD.jfif";
import TillerPrototypeImg from "./tiller - prototype.jfif";
import UkuleleCADImg from "./Ukulele1.png";
import TireRimImg from "./Rim1.png";
import EngineImg from "./4 piston engine.jfif";
import PBRHolderImg from "./PBRholder.png";

function Portfolio() {
    return (
        <section className="portfolio">
            <Helmet>
                <title>Portfolio</title>
            </Helmet>

            <div className="portfolio-hero">
                <ProfileOverview />
                <p className="portfolio-intro">
                    Mechanical engineering junior with a passion for manufacturing and design - raised
                    around my family's kitchen-appliance manufacturing business, which shaped an early
                    interest in how things are actually built. I interned at General Stamping &
                    Metalworks (GSM), a precision metal stamping and fabrication company, where I
                    designed production weld fixtures in SolidWorks and led internal
                    continuous-improvement projects - including a coolant-recapture system and a
                    vision-inspection cart - that cut waste and improved process reliability on the
                    shop floor. I also took on independent design projects of my own, from reverse
                    engineering to CAD modeling, outside of coursework. I'm looking for co-op or
                    internship opportunities for 2027.
                </p>
            </div>

            <div className="portfolio-section">
                <h2 className="portfolio-section-heading">Skills</h2>
                <ul className="portfolio-skills">
                    <li>SolidWorks</li>
                    <li>CAD</li>
                    <li>GD&T</li>
                    <li>Tolerance stack-up</li>
                    <li>DXF</li>
                    <li>DFM/DFA</li>
                    <li>PPAP</li>
                    <li>Poka-yoke</li>
                    <li>Progressive fixture design</li>
                    <li>Plex (ERP/MES)</li>
                    <li>CNC machining</li>
                    <li>G-code</li>
                    <li>Vision-system integration</li>
                    <li>Reverse engineering</li>
                    <li>3D printing</li>
                    <li>Wire Arc Additive Manufacturing (WAAM)</li>
                    <li>Tecplot 360</li>
                    <li>Python</li>
                    <li>Fortran</li>
                    <li>MATLAB</li>
                    <li>MS Excel</li>
                    <li>monday.com</li>
                </ul>
            </div>

            <div className="portfolio-section">
                <h2 className="portfolio-section-heading">Projects</h2>
                <div className="portfolio-projects">
                    <PortfolioProject
                        title="Weld Fixture Design - Agricultural & Heavy Equipment OEM"
                        context="General Stamping & Metalworks - Agricultural & Heavy Equipment OEM Program - Manufacturing Engineering Intern - June 2026 - July 2026"
                        objective="Designed a weld fixture for a production part supplied to a global agricultural & heavy equipment OEM, to accurately locate and clamp it for production welding."
                        contribution="Independently designed the fixture in SolidWorks: placed toggle clamps for repeatable clamping force, defined the locating scheme, and sized the base plate to match production workholding standards."
                        technicalDetail={[
                            "Toggle clamps at each end fully constrained the part without blocking the weld path.",
                            "Locating features pulled directly from the part print for cycle-to-cycle repeatability.",
                            "Base plate sized to the plant's standard fixture-mounting layout."
                        ]}
                        result="The fixture went into active use on the production line, holding the part accurately for every weld cycle."
                        tags={["SolidWorks", "GD&T", "Locating scheme design", "Weldment fixturing"]}
                        atAGlance={[
                            { label: "Industry", value: "Agricultural & heavy equipment OEM" },
                            { label: "Software", value: "SolidWorks" }
                        ]}
                        images={["Fixture - SolidWorks (part removed for confidentiality)"]}
                        imageFiles={[AgOEMFixtureImg]}
                    />

                    <PortfolioProject
                        title="Progressive Fixture Design (Tack + Final Weld) - Turf & Outdoor Power Equipment Manufacturer"
                        context="General Stamping & Metalworks - Turf & Outdoor Power Equipment Program - Manufacturing Engineering Intern - June 2026 - July 2026"
                        objective="Designed a two-station progressive fixture for a commercial turf & outdoor power equipment manufacturer's part, welded by a cobot whose torch couldn't reach every joint from one orientation - station 1 tacked what was reachable, station 2 finished the rest."
                        contribution="Independently designed both stations in SolidWorks. Revised station 1's clamp and locating layout after an early version let the part shift, then built station 2 to pick the part up from its tack datum on locating bushings."
                        technicalDetail={[
                            "Station 1 (tack): clamps and pins held the part in the cobot-reachable orientation; the initial design let it shift, the revised layout fixed it.",
                            "Station 2 (final weld): two bushings picked the part up from its tack datum; a single clamp left the remaining joints open to the torch.",
                            "Shared base-plate mounting layout kept one consistent part datum across both stations."
                        ]}
                        result="Both stations went into active use, letting the cobot complete every joint despite reach limits at any single orientation."
                        tags={["SolidWorks", "Progressive fixture design", "Robot reach/access analysis", "Multi-station tooling"]}
                        atAGlance={[
                            { label: "Industry", value: "Turf & outdoor power equipment manufacturer" },
                            { label: "Stations", value: "2 - tack, then final weld" },
                            { label: "Software", value: "SolidWorks" }
                        ]}
                        images={[
                            "Fixture - SolidWorks (part removed for confidentiality)",
                            "Fixture - alternate view, SolidWorks (part removed for confidentiality)",
                            "Fixture - top view, SolidWorks (part removed for confidentiality)"
                        ]}
                        imageFiles={[TurfProgressiveFixtureImg1, TurfProgressiveFixtureImg2, TurfProgressiveFixtureImg3]}
                    />

                    <PortfolioProject
                        title="Weld + Check Fixture Design (Poka-Yoke) - Turf & Outdoor Power Equipment Manufacturer"
                        context="General Stamping & Metalworks - Turf & Outdoor Power Equipment Program - Manufacturing Engineering Intern - June 2026 - July 2026"
                        objective="Designed a combined weld-and-check fixture for a commercial turf & outdoor power equipment manufacturer's part - a poka-yoke (mistake-proofing) design that located, clamped, and dimensionally verified the part in one tool, so an out-of-spec part couldn't be welded."
                        contribution="Independently designed the fixture in SolidWorks, combining weld-locating and poka-yoke check features on a single base plate defined directly from the part print."
                        technicalDetail={[
                            "Toggle clamps at each end fully constrained the part in a fixed orientation before welding.",
                            "Poka-yoke features only let the part seat if it matched print, blocking an out-of-spec part from being welded.",
                            "One tool combined locating and checking, cutting the handling steps per part."
                        ]}
                        result="The fixture went into active use, error-proofing the weld setup for every part on the line."
                        tags={["SolidWorks", "Poka-yoke (mistake-proofing)", "Go/no-go gauging", "Lean manufacturing", "Error-proofing design"]}
                        atAGlance={[
                            { label: "Industry", value: "Turf & outdoor power equipment manufacturer" },
                            { label: "Function", value: "Weld locating + dimensional check" },
                            { label: "Software", value: "SolidWorks" }
                        ]}
                        images={["Weld + check fixture (poka-yoke) - SolidWorks (part removed for confidentiality)"]}
                        imageFiles={[TurfPokaYokeFixtureImg]}
                    />

                    <PortfolioProject
                        title="Weld Fixture Design (Left & Right Parts) - Turf & Outdoor Power Equipment Manufacturer"
                        context="General Stamping & Metalworks - Turf & Outdoor Power Equipment Program - Manufacturing Engineering Intern - June 2026 - July 2026"
                        objective="Designed weld fixtures for the left- and right-hand variants of a commercial turf & outdoor power equipment manufacturer's part, to accurately locate and clamp each for production welding."
                        contribution="Independently designed both fixtures in SolidWorks, mirroring the locating and clamping scheme from the part print to handle the left/right asymmetry."
                        technicalDetail={[
                            "Toggle clamps at each end fully constrained each part variant without blocking the weld path.",
                            "Locating features mirrored between the left- and right-hand fixtures, pulled directly from the part print for cycle-to-cycle repeatability.",
                            "Base plates sized to the plant's standard fixture-mounting layout for both variants."
                        ]}
                        result="Both fixtures went into active use on the production line, holding the left and right parts accurately for every weld cycle."
                        tags={["SolidWorks", "GD&T", "Locating scheme design", "Weldment fixturing", "Left/right part variants"]}
                        atAGlance={[
                            { label: "Industry", value: "Turf & outdoor power equipment manufacturer" },
                            { label: "Variants", value: "Left & right parts" },
                            { label: "Software", value: "SolidWorks" }
                        ]}
                        images={[
                            "Left-part fixture - SolidWorks (part removed for confidentiality)",
                            "Right-part fixture - SolidWorks (part removed for confidentiality)"
                        ]}
                        imageFiles={[TurfLeftRightFixtureImg1, TurfLeftRightFixtureImg2]}
                    />

                    <PortfolioProject
                        title="Coolant Tray Design - Continuous Improvement"
                        context="General Stamping & Metalworks - Internal project - Manufacturing Engineering Intern - June 2026 - July 2026"
                        objective="Internal continuous improvement project: designed coolant trays for a roller conveyor to capture cutting coolant that was being lost, cutting waste and cost."
                        contribution="Independently designed the tray in SolidWorks to retrofit the existing conveyor, sizing and positioning it to catch runoff and route it back into the system."
                        technicalDetail={[
                            "Tray geometry matched to the conveyor's width and roller spacing for a drop-in fit - no structural changes needed.",
                            "Positioned at the exact points coolant was reaching the floor.",
                            "Built as part of a shop-wide effort to cut waste and material cost."
                        ]}
                        result="Reduced coolant waste and cost, saving an estimated $7,600/year in recovered coolant; went into use on the production floor."
                        tags={["SolidWorks", "Sheet-metal design", "Retrofit design for existing equipment", "Waste-stream reduction", "Cost-benefit analysis"]}
                        atAGlance={[
                            { label: "Project type", value: "Internal project" },
                            { label: "Program", value: "Continuous improvement (CI)" },
                            { label: "Focus", value: "Coolant capture and reuse" },
                            { label: "Savings", value: "$7,600/year (coolant recovery)" }
                        ]}
                        images={[
                            "Roller conveyor with coolant trays - isometric view, SolidWorks",
                            "Roller conveyor with coolant trays - side view, SolidWorks"
                        ]}
                        imageFiles={[CoolantTrayIsoImg, CoolantTraySideImg]}
                    />

                    <PortfolioProject
                        title="PBR - 3D-Printed Cobot Part Holder"
                        context="General Stamping & Metalworks - PBR - Manufacturing Engineering Intern - June 2026 - July 2026"
                        objective="Designed a stacking part holder for a PBR brake-line automation cell that a cobot could pick from using vacuum suction cups, without paying for a machined metal fixture."
                        contribution="Independently designed the holder in SolidWorks and produced it by 3D printing instead of routing it through the tool room for a metal build."
                        technicalDetail={[
                            "Rib dividers formed individual pockets that stacked multiple parts in a fixed, repeatable orientation for the cobot's vacuum suction cups to pick from.",
                            "Base plate's hole pattern mounted directly to the cobot cell's pegboard for quick, repeatable placement.",
                            "3D printed in-house instead of machined from metal, skipping the tool room build queue entirely.",
                            "Metal version quoted at roughly $2,000; 3D-printed version built for a fraction of that in plastic."
                        ]}
                        result="Cut fixture cost from an estimated $2,000 in metal to a fraction of that in 3D-printed material, while stacking parts for faster cobot picking."
                        tags={["SolidWorks", "3D printing", "Cobot end-of-arm tooling", "Cost reduction", "Vacuum-gripper fixturing"]}
                        atAGlance={[
                            { label: "Customer", value: "PBR" },
                            { label: "Cost savings", value: "~$2,000 (metal) → 3D-printed" },
                            { label: "Function", value: "Stacked part holder for cobot vacuum pickup" }
                        ]}
                        images={["PBR part holder - SolidWorks"]}
                        imageFiles={[PBRHolderImg]}
                    />

                    <PortfolioProject
                        title="INPR Vision-Inspection Cart"
                        context="General Stamping & Metalworks - Internal project, Insertion Press (INPR) - Manufacturing Engineering Intern - June 2026 - July 2026"
                        objective="Internal continuous improvement project on the insertion press (INPR) process: designed a mobile vision-inspection cart to verify insertions and catch defects earlier."
                        contribution="Designed the cart and fixture in SolidWorks: the mobile frame, a repeatable part-holding surface, and the overhead mount for the vision sensor."
                        technicalDetail={[
                            "Casters let the cart be staged at the insertion press station as needed.",
                            "Overhead frame sized the sensor's field of view to fully capture the part.",
                            "Fixture shelf held the part in a fixed, repeatable position for consistent readings."
                        ]}
                        result="Improved inspection consistency on the insertion press process; went into daily use."
                        tags={["SolidWorks", "Vision-system integration", "Mobile equipment design", "Process inspection", "Ergonomic layout"]}
                        atAGlance={[
                            { label: "Project type", value: "Internal project" },
                            { label: "Process", value: "Insertion press (INPR)" },
                            { label: "Function", value: "Vision-based part inspection" }
                        ]}
                        images={[
                            "INPR vision-inspection cart - SolidWorks",
                            "INPR vision-inspection cart - camera coverage area, SolidWorks"
                        ]}
                        imageFiles={[INPRImg, INPRCoverageImg]}
                    />

                    <PortfolioProject
                        title="Human-Powered Tiller Design"
                        context="Iowa State University, ME 2700 - Classroom Team Project - Aug 2025 - Dec 2025"
                        objective="Designed and built a human-powered tiller (ME 2700) that maximized soil-turning performance within a strict $150 material budget and full BOM/DFM documentation."
                        contribution="Led mechanical design and material selection in SolidWorks; owned the DFM/DFA analysis and the full bill of materials to stay on budget."
                        technicalDetail={[
                            "CAD model used to tolerance and validate every custom part before cutting metal.",
                            "Fabricated parts on the lathe, mill, bandsaw, and drill press, designing for machining feasibility from the start."
                        ]}
                        result="Delivered a fully functional, budget-compliant prototype that passed every performance check."
                        tags={["SolidWorks", "DFM/DFA", "Machining (lathe, mill, bandsaw, drill press)", "Budget-constrained design", "BOM development"]}
                        atAGlance={[
                            { label: "Team", value: "ME 2700 class team" },
                            { label: "Budget", value: "$150 material budget" },
                            { label: "Software", value: "SolidWorks" }
                        ]}
                        images={[
                            "Tiller - SolidWorks CAD model",
                            "Finished prototype in field testing"
                        ]}
                        imageFiles={[TillerCADImg, TillerPrototypeImg]}
                        documents={[
                            { label: "Design Expo Poster (PDF)", href: TillerDesignExpoPoster }
                        ]}
                    />

                    <PortfolioProject
                        title="Makala Soprano Ukulele - Reverse Engineering"
                        context="Iowa State University - Outside-of-classroom Project, Model MK-S - Jul 2025 - Aug 2025"
                        objective="Reverse-engineered a Makala soprano ukulele into a precise, manufacturable CAD model, capturing exact geometry and surface detail."
                        contribution="Measured and modeled the instrument in SolidWorks, rebuilding its complex curved surfaces from direct physical measurement."
                        technicalDetail={[
                            "High-fidelity model built entirely from measurement of the original instrument.",
                            "3D-printed replica used to verify geometry and surface accuracy."
                        ]}
                        result="Produced a replica that closely matched the original's geometry and surface detail."
                        tags={["SolidWorks", "Reverse engineering", "Surface modeling", "Metrology", "3D printing"]}
                        atAGlance={[
                            { label: "Method", value: "Reverse-engineered from a physical instrument" },
                            { label: "Output", value: "High-fidelity CAD model + 3D-printed replica" },
                            { label: "Software", value: "SolidWorks" }
                        ]}
                        images={["Ukulele - SolidWorks CAD model"]}
                        imageFiles={[UkuleleCADImg]}
                    />

                    <PortfolioProject
                        title="Solar Car Tire Rim Design"
                        context="Iowa State University, PRISM Team - Outside-of-classroom Project - Jun 2025 - Jul 2025"
                        objective="Designed a lightweight tire rim for the PRISM solar car team, balancing structural strength against cost and weight under real driving loads."
                        contribution="Designed and 3D printed a custom rim in SolidWorks, applying structural and cost trade-off analysis to the geometry."
                        technicalDetail={[
                            "Mechanical features and mates modeled to reflect real-world component interaction.",
                            "Structural and cost trade-offs guided material and geometry choices."
                        ]}
                        result="Delivered a rim that met the team's performance requirements at lower cost and weight."
                        tags={["SolidWorks", "Structural design", "Weight optimization", "3D printing", "Cost-performance trade-off analysis"]}
                        atAGlance={[
                            { label: "Team", value: "PRISM solar car team" },
                            { label: "Method", value: "Designed in SolidWorks, 3D printed" },
                            { label: "Priorities", value: "Structural strength, cost, weight" }
                        ]}
                        images={["PRISM tire rim - SolidWorks CAD model"]}
                        imageFiles={[TireRimImg]}
                    />

                    <PortfolioProject
                        title="Four-Cylinder Piston Engine Assembly"
                        context="Iowa State University - Outside-of-classroom Project - Jun 2025 - Jun 2025"
                        objective="Modeled a 4-cylinder piston engine assembly from scratch to build a mechanically accurate representation of combustion-engine motion."
                        contribution="Modeled every component - crankshaft, pistons, hardware - in SolidWorks and assembled them with mates that reproduced true engine motion."
                        technicalDetail={[
                            "Precise modeling reproduced realistic component interaction and clearances.",
                            "Mates configured to simulate crankshaft/piston motion through a full combustion cycle."
                        ]}
                        result="Produced a mechanically accurate assembly demonstrating real internal-combustion component interaction."
                        tags={["SolidWorks", "Assembly modeling", "Mechanism design", "Tolerancing"]}
                        atAGlance={[
                            { label: "Components modeled", value: "Crankshaft, pistons, connecting hardware" },
                            { label: "Motion simulated", value: "Full combustion cycle" },
                            { label: "Software", value: "SolidWorks" }
                        ]}
                        images={["4-cylinder piston engine - SolidWorks assembly"]}
                        imageFiles={[EngineImg]}
                    />
                </div>
            </div>
        </section>
    );
}

export default Portfolio;
